// Where an overlay has to render to be seen.
//
// showModal() promotes a <dialog> into the top layer, and inside that layer
// z-index stops meaning anything: the dialog's ::backdrop paints above every
// element outside the dialog's subtree no matter how high that element is
// stacked. Measured in Chromium, re-promoting does not help and neither does
// the other route into the layer - a popover=manual shown after the dialog
// still renders under its backdrop. The only thing that paints above a modal
// dialog is a node inside that dialog. So an overlay that has to survive an
// open dialog renders into the dialog rather than into <body>.

import { useSyncExternalStore } from 'react';

/**
 * overlayHost returns the portal container for an overlay anchored to `node`:
 * the dialog `node` sits in, or the document body when it sits on the page.
 * Use it for anything painted outside its own DOM box - a listbox, a popover,
 * a tooltip - so the same component works on a page and in a dialog.
 *
 * Safe to call during render. It reads the DOM and holds no state.
 */
export function overlayHost(node: Element | null): HTMLElement {
  return node?.closest('dialog') ?? document.body;
}

// The unanchored case. A toast has no element inside the dialog to hang off,
// so Modal reports the dialog it opened and the stack follows it. Reading
// `document.querySelector('dialog[open]')` at push time would not do: the
// common call raises the toast and closes the dialog in the same breath, and
// a stack pinned to a dialog that is closing would go out with it.

let stack: readonly HTMLDialogElement[] = [];
const listeners = new Set<() => void>();

function emit(): void {
  for (const notify of listeners) notify();
}

/** pushTopLayerHost registers a dialog as the innermost open one. */
export function pushTopLayerHost(el: HTMLDialogElement): void {
  stack = [...stack, el];
  emit();
}

/** popTopLayerHost deregisters a dialog. A dialog not registered is a no-op. */
export function popTopLayerHost(el: HTMLDialogElement): void {
  const next = stack.filter((h) => h !== el);
  if (next.length === stack.length) return;
  stack = next;
  emit();
}

function subscribe(notify: () => void): () => void {
  listeners.add(notify);
  return () => {
    listeners.delete(notify);
  };
}

function snapshot(): HTMLDialogElement | null {
  return stack.length > 0 ? stack[stack.length - 1] : null;
}

/**
 * useTopLayerHost returns the innermost open modal dialog, or null when none
 * is open. The value is an element identity, so it is stable between renders
 * and safe to hand back from a useSyncExternalStore snapshot.
 */
export function useTopLayerHost(): HTMLDialogElement | null {
  return useSyncExternalStore(subscribe, snapshot, () => null);
}
